import React from 'react';
import { ArrowLeft } from 'lucide-react';

interface PrivacyPolicyProps {
  onBack: () => void;
}

export default function PrivacyPolicy({ onBack }: PrivacyPolicyProps) {
  return (
    <div className="min-h-screen bg-white text-zinc-900 selection:bg-purple-100 selection:text-purple-900 relative overflow-hidden font-sans">
      {/* Colorful Gradient Backdrop */}
      <div className="absolute inset-x-0 top-0 h-[600px] pointer-events-none overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-15%] w-[55%] h-[60%] rounded-full bg-purple-200/35 blur-[120px]" />
        <div className="absolute top-[-5%] right-[-10%] w-[50%] h-[55%] rounded-full bg-orange-100/40 blur-[110px]" />
        <div className="absolute top-[20%] left-[25%] w-[45%] h-[50%] rounded-full bg-blue-100/30 blur-[130px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12 md:py-20">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="group flex items-center gap-2 text-sm font-semibold text-zinc-550 hover:text-zinc-950 transition-colors mb-12 cursor-pointer"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>

        {/* Article Container */}
        <article className="space-y-10">
          <header className="space-y-4 border-b border-zinc-100 pb-8">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-3 py-1 text-xs font-bold text-purple-700 uppercase">
              BETA Platform
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-zinc-950">
              Privacy Policy
            </h1>
            <p className="text-sm font-semibold text-zinc-400">
              Last Updated: June 26, 2026
            </p>
          </header>

          <section className="space-y-6 text-[15px] leading-relaxed text-zinc-650 font-medium">
            <p>
              Welcome to <strong>Design Studio</strong>. Your privacy is of paramount importance to us. This Privacy Policy details how we handle information in connection with your use of our platform, services, and website.
            </p>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">1. Ownership and Administration</h2>
            <p>
              Design Studio is owned, administered, developed, and maintained by <strong>Caselgrid</strong>. All software components, documentation, assets, and design concepts associated with the Design Studio suite remain the exclusive intellectual property of Caselgrid.
            </p>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">2. Beta Software and Data Retention</h2>
            <p>
              Please note that Design Studio is currently in its <strong>BETA release phase</strong> and is undergoing active software engineering. As a result:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Data storage structures, document caches, and account credentials may be modified, reset, or cleared at any time during ongoing system upgrades.</li>
              <li>We make no guarantees regarding the continuous availability, security, or integrity of user-generated mockups, text blocks, drawings, or configurations.</li>
              <li>We collect essential telemetry and platform diagnostics (such as browser type, feature engagement, and error logs) to debug runtime exceptions and optimize layouts.</li>
            </ul>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">3. Data Collection and Usage</h2>
            <p>
              Design Studio collects standard workspace content strictly to deliver the interactive whiteboard, flowchart, and doc editing services. No private workspace elements are sold or redistributed. Content sync is local-first, utilizing optional network endpoints only when explicit cloud synchronization features are toggled by the user.
            </p>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">4. User Risk and Disclaimer of Liability</h2>
            <p className="bg-red-50/50 border border-red-100 rounded-2xl p-5 text-red-950 text-sm">
              <strong>IMPORTANT LEGAL NOTICE:</strong> Because the platform is in Beta and maintained as a test environment, your utilization of Design Studio is <strong>entirely at your own risk</strong>. Under no circumstances shall Caselgrid, its affiliates, developers, or administrators be liable for any direct, indirect, incidental, or consequential damages, including but not limited to loss of data, production interruption, or proprietary design leaks resulting from security breaches or software defects.
            </p>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">5. Revisions</h2>
            <p>
              Caselgrid reserves the right to modify this Privacy Policy at any time. Changes become effective immediately upon being published on this page. Your continued use of the platform constitutes agreement to the updated terms.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
}
